import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { UserProfile } from '../types';
import { Session } from '@supabase/supabase-js';
import { useAuth } from '../components/auth/AuthProvider';
import { runtimeCache } from '../lib/runtimeCache';
import { normalizeLocationsOnLoad } from '../lib/preference-normalizers';

// Extract names from profile - ONLY uses profiles table fields (first_name/last_name or firstName/lastName)
// Does NOT fall back to user_metadata or other sources
// Preserves NULL values (does not default to empty string)
export function getNamesAny(profile: any) {
    // Only check profiles table fields - preserve NULL, don't fall back to empty string
    const first = profile?.first_name ?? profile?.firstName ?? null;
    const last = profile?.last_name ?? profile?.lastName ?? null;
    // Trim only if not null, otherwise keep null
    const firstTrimmed = first !== null && first !== undefined ? String(first).trim() : null;
    const lastTrimmed = last !== null && last !== undefined ? String(last).trim() : null;
    return { 
        first: firstTrimmed || null, 
        last: lastTrimmed || null, 
        hasNames: !!(firstTrimmed && lastTrimmed && firstTrimmed.length > 0 && lastTrimmed.length > 0) 
    };
}

// Single source of truth for profile completion check
// ONLY uses profiles table fields (first_name/last_name or firstName/lastName)
// Does NOT use user_metadata or other sources
export function isProfileCompleteAny(profile: any): boolean {
    if (!profile) return false;
    
    // Use getNamesAny to check names (ONLY checks profiles table fields)
    const { hasNames } = getNamesAny(profile);
    return hasNames;
}

// Normalize DB profile row (snake_case) to app profile state (camelCase)
// CRITICAL: ONLY uses profiles table fields - preserves NULL values
// Does NOT fall back to user_metadata, name field, or other sources
function normalizeProfile(row: any): UserProfile & { first_name?: string | null; last_name?: string | null } {
    // ONLY use profiles table fields - preserve NULL, don't fall back
    const firstName = row.first_name ?? row.firstName ?? null;
    const lastName = row.last_name ?? row.lastName ?? null;
    
    // Build name from first/last only if both exist, otherwise use row.name or empty
    const name = (firstName && lastName) 
        ? `${firstName} ${lastName}`.trim()
        : (row.name || '');
    
    return {
        name,
        // camelCase fields (primary for app usage) - preserve NULL
        firstName: firstName !== null && firstName !== undefined ? String(firstName).trim() : null,
        lastName: lastName !== null && lastName !== undefined ? String(lastName).trim() : null,
        // snake_case fields (keep for DB compatibility) - preserve NULL
        first_name: row.first_name ?? null,
        last_name: row.last_name ?? null,
        email: row.email,
        phone: row.phone,
        avatar: row.avatar,
        
        isConfigured: !!row.buyer_type,
        buyerType: row.buyer_type,
        budgetRange: row.budget_range,
        preferredLocations: normalizeLocationsOnLoad(row.preferred_locations), // Normalize on load to handle legacy values
        propertyTypes: row.property_types || [],
        investmentGoals: row.investment_goals || [],
        
        aiSummary: row.ai_summary,
        lastUpdated: row.last_updated
    };
}

export const useProfile = (session: Session | null) => {
    const { authReady, userId } = useAuth();
    const [profile, setProfile] = useState<UserProfile | null>(null);
    // CRITICAL: loading starts as true, becomes false only after fetch completes (success or error)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [timeoutError, setTimeoutError] = useState<string | null>(null);

    const fetchProfile = useCallback(async () => {
        // Return immediately if !authReady or !userId
        if (!authReady || !userId) {
            if (import.meta.env.DEV) {
                console.log('[useProfile] [GUARD] Skipping fetch:', { authReady, userId });
            }
            setLoading(false); // Mark as not loading if we can't fetch
            return;
        }

        // Don't fetch if no session/user
        if (!session?.user) {
            setProfile(null);
            setLoading(false); // Mark as not loading if no session
            return;
        }

        // Use runtime cache to check if we should fetch - wrap in try/catch to prevent crashes
        let shouldFetch = true;
        try {
            shouldFetch = runtimeCache.shouldFetch('profile', userId);
        } catch (err) {
            console.error('[useProfile] Cache read error:', err);
            // Continue with fetch if cache read fails
            shouldFetch = true;
        }

        if (!shouldFetch) {
            if (import.meta.env.DEV) {
                try {
                    const state = runtimeCache.getState();
                    console.log('[useProfile] [GUARD] Skipping fetch via cache:', {
                        loaded: state.loaded.profile,
                        inFlight: state.inFlight.profile,
                        cacheUserId: state.userId
                    });
                } catch (err) {
                    console.error('[useProfile] Cache state read error:', err);
                }
            }
            setLoading(false); // Mark as not loading if cached
            return;
        }

        try {
            // Wrap cache operations in try/catch to prevent crashes
            try {
                runtimeCache.markInFlight('profile', userId);
            } catch (err) {
                console.error('[useProfile] Cache markInFlight error:', err);
                // Continue with fetch even if cache mark fails
            }
            
            setLoading(true);
            if (import.meta.env.DEV) console.log('[useProfile] [FETCH START] Profile for user', userId);
            
            // CRITICAL FIX: Use .maybeSingle() instead of .single() to handle cases where
            // multiple rows might exist (shouldn't happen after migration, but defensive coding)
            // .single() throws error if 0 or 2+ rows found
            // .maybeSingle() returns null if 0 rows, first row if 1 row, or throws if 2+ rows
            // After migration, there should only be 1 row, but we handle edge cases gracefully
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', session.user.id)
                .maybeSingle();

            if (error) {
                // PGRST116 = "no rows returned" - this is fine, user doesn't have a profile yet
                // With .maybeSingle(), we shouldn't get PGRST116, but handle it defensively
                const isNotFound = error.code === 'PGRST116';
                const isAuthError = error.code === '42501' || 
                                    error.code === 'PGRST301' || 
                                    (error.message && (error.message.includes('JWT') || error.message.includes('permission') || error.message.includes('unauthorized')));
                
                // If multiple rows exist (shouldn't happen after migration), log warning
                if (error.message && error.message.includes('multiple rows')) {
                    console.error('[useProfile] CRITICAL: Multiple profile rows found for user', userId, '- this should not happen after migration');
                }
                
                if (isNotFound || isAuthError) {
                    if (import.meta.env.DEV) {
                        console.log('[useProfile] Profile not found or unauthorized, skipping retry');
                        if (error.code) console.log('[useProfile] Error code:', error.code);
                    }
                    // CRITICAL: Mark cache as loaded BEFORE setState to prevent re-triggers
                    try {
                        runtimeCache.markLoaded('profile', userId);
                    } catch (err) {
                        console.error('[useProfile] Cache markLoaded error:', err);
                    }
                    setProfile(null);
                    setLoading(false); // CRITICAL: Ensure loading is set to false
                    return;
                }
                throw error;
            }

            if (data) {
                // Normalize DB row (snake_case) to app profile (camelCase)
                const normalizedProfile = normalizeProfile(data);
                
                // Auto-populate missing profile data from user_metadata and session
                const metadata = session?.user?.user_metadata || {};
                const needsNameUpdate = !normalizedProfile.firstName || !normalizedProfile.lastName;
                const needsEmailUpdate = !normalizedProfile.email && session?.user?.email;
                
                if ((needsNameUpdate || needsEmailUpdate) && session?.user) {
                    let firstName: string | null = normalizedProfile.firstName || null;
                    let lastName: string | null = normalizedProfile.lastName || null;
                    
                    // Extract names from metadata if missing
                    if (needsNameUpdate) {
                        // Priority: given_name/family_name > first_name/last_name > full_name > name
                        if (metadata.given_name || metadata.family_name) {
                            firstName = metadata.given_name?.trim() || firstName;
                            lastName = metadata.family_name?.trim() || lastName;
                        } else if (metadata.first_name || metadata.last_name) {
                            firstName = metadata.first_name?.trim() || firstName;
                            lastName = metadata.last_name?.trim() || lastName;
                        } else if (metadata.full_name || metadata.name) {
                            const fullName = (metadata.full_name || metadata.name || '').trim();
                            if (fullName) {
                                const parts = fullName.split(/\s+/);
                                if (parts.length === 1) {
                                    firstName = firstName || parts[0];
                                } else {
                                    firstName = firstName || parts[0];
                                    lastName = lastName || parts.slice(1).join(' ');
                                }
                            }
                        }
                    }
                    
                    // Build update data for missing fields
                    const updateData: any = { id: userId };
                    let hasUpdates = false;
                    
                    if (!normalizedProfile.firstName && firstName) {
                        updateData.first_name = firstName;
                        hasUpdates = true;
                    }
                    if (!normalizedProfile.lastName && lastName) {
                        updateData.last_name = lastName;
                        hasUpdates = true;
                    }
                    if (!normalizedProfile.email && session.user.email) {
                        updateData.email = session.user.email;
                        hasUpdates = true;
                    }
                    
                    // Only update if we have something to update
                    if (hasUpdates) {
                        (async () => {
                            try {
                                const { error: upsertError } = await supabase.from('profiles')
                                    .upsert(updateData, { onConflict: 'id' });
                                
                                if (upsertError) {
                                    if (import.meta.env.DEV) {
                                        console.warn('[useProfile] Failed to auto-populate profile:', upsertError);
                                    }
                                } else {
                                    // Update local state with new data
                                    const updatedProfile = {
                                        ...normalizedProfile,
                                        firstName: updateData.first_name || normalizedProfile.firstName,
                                        lastName: updateData.last_name || normalizedProfile.lastName,
                                        email: updateData.email || normalizedProfile.email,
                                        name: (updateData.first_name || normalizedProfile.firstName) && (updateData.last_name || normalizedProfile.lastName)
                                            ? `${updateData.first_name || normalizedProfile.firstName} ${updateData.last_name || normalizedProfile.lastName}`
                                            : normalizedProfile.name
                                    };
                                    setProfile(updatedProfile);
                                    if (import.meta.env.DEV) {
                                        console.log('[useProfile] Auto-populated profile from session/metadata:', updateData);
                                    }
                                }
                            } catch (err) {
                                if (import.meta.env.DEV) {
                                    console.warn('[useProfile] Error auto-populating profile:', err);
                                }
                            }
                        })();
                    }
                }
                
                // If email is still missing in normalized profile but exists in session, update local state
                if (!normalizedProfile.email && session?.user?.email) {
                    normalizedProfile.email = session.user.email;
                }
                
                // CRITICAL: Mark cache as loaded BEFORE setState to prevent re-triggers
                try {
                    runtimeCache.markLoaded('profile', userId);
                } catch (err) {
                    console.error('[useProfile] Cache markLoaded error:', err);
                }
                setProfile(normalizedProfile);
                if (import.meta.env.DEV) {
                    console.log('[useProfile] [FETCH END] Profile loaded:', {
                        first_name: data.first_name,
                        last_name: data.last_name,
                        firstName: normalizedProfile.firstName,
                        lastName: normalizedProfile.lastName,
                        isComplete: isProfileCompleteAny(normalizedProfile)
                    });
                }

                // Check if this is a newly created profile (created within last 5 minutes) and send signup notification
                // This ensures we only notify once per new user
                try {
                    const profileCreatedAt = data.created_at ? new Date(data.created_at).getTime() : null;
                    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
                    const isNewProfile = profileCreatedAt && profileCreatedAt > fiveMinutesAgo;
                    
                    // Check if we've already sent notification for this user
                    const notificationKey = `signup_notified_${userId}`;
                    const alreadyNotified = localStorage.getItem(notificationKey);
                    
                    if (isNewProfile && !alreadyNotified && session?.user) {
                        // Import notifyAdmin dynamically to avoid circular dependencies
                        import('../lib/notify').then(({ notifyAdmin }) => {
                            notifyAdmin('signup', {
                                userId: session.user.id,
                                email: session.user.email || data.email || '',
                                provider: session.user.app_metadata?.provider || 'email',
                                firstName: normalizedProfile.firstName || '',
                                lastName: normalizedProfile.lastName || '',
                                createdAt: data.created_at || new Date().toISOString()
                            });
                            // Mark as notified to prevent duplicates
                            localStorage.setItem(notificationKey, 'true');
                        }).catch(err => {
                            if (import.meta.env.DEV) {
                                console.error('[useProfile] Failed to send signup notification:', err);
                            }
                        });
                    }
                } catch (err) {
                    if (import.meta.env.DEV) {
                        console.error('[useProfile] Error checking for new profile notification:', err);
                    }
                }
            } else {
                // CRITICAL: Mark cache as loaded BEFORE setState
                try {
                    runtimeCache.markLoaded('profile', userId);
                } catch (err) {
                    console.error('[useProfile] Cache markLoaded error:', err);
                }
                setProfile(null);
                if (import.meta.env.DEV) console.log('[useProfile] [FETCH END] Profile not found (null)');
            }
        } catch (err: any) {
            console.error('[useProfile] Error fetching profile:', err);
            if (import.meta.env.DEV) {
                console.error('[useProfile] Error details:', {
                    message: err?.message,
                    code: err?.code
                });
            }
            // Mark as failed but allow retry later if needed
            try {
                runtimeCache.markFailed('profile', userId);
            } catch (cacheErr) {
                console.error('[useProfile] Cache markFailed error:', cacheErr);
            }
            // Do NOT set error state that triggers another fetch
        } finally {
            // CRITICAL: Always set loading to false, even if errors occur
            setLoading(false);
        }
    }, [session, authReady, userId]);

    const updateProfile = async (updates: Partial<UserProfile>): Promise<UserProfile> => {
        if (!session?.user || !authReady || !userId) {
            throw new Error('Cannot update profile: session or auth not ready');
        }

        try {
            setLoading(true);
            
            // CRITICAL FIX: Use api.user.updateProfile which handles ALL fields including buyer preferences
            // This ensures buyer preferences (buyerType, budgetRange, preferredLocations, etc.) are saved
            const { api } = await import('../services/api');
            
            // Import api at top level for force refetch
            const apiService = api;
            
            // Get current session user for email/name fallbacks
            const currentUser = session.user;
            
            // Merge updates with current profile to create full profile object
            // If profile doesn't exist yet, create a minimal one from user data
            const baseProfile: UserProfile = profile || {
                name: currentUser.user_metadata?.full_name || currentUser.email?.split('@')[0] || 'User',
                firstName: currentUser.user_metadata?.first_name || currentUser.user_metadata?.given_name || null,
                lastName: currentUser.user_metadata?.last_name || currentUser.user_metadata?.family_name || null,
                email: currentUser.email || '',
                phone: '',
                avatar: currentUser.user_metadata?.avatar_url || '',
                isConfigured: false,
                buyerType: 'Investor',
                budgetRange: '',
                preferredLocations: [],
                propertyTypes: [],
                investmentGoals: [],
                aiSummary: '',
                lastUpdated: new Date().toLocaleDateString(),
                // Notification preferences defaults
                emailNotifications: {
                    newProperties: true,
                    priceDrops: true,
                    marketUpdates: true,
                },
                smsNotifications: {
                    urgentDeals: false,
                    callReminders: true,
                },
                notificationFrequency: 'real-time'
            };
            
            const fullProfile: UserProfile = {
                ...baseProfile,
                ...updates,
                // Ensure arrays are never null
                preferredLocations: updates.preferredLocations ?? (baseProfile.preferredLocations ?? []),
                propertyTypes: updates.propertyTypes ?? (baseProfile.propertyTypes ?? []),
                investmentGoals: updates.investmentGoals ?? (baseProfile.investmentGoals ?? []),
                // Ensure email is always set
                email: updates.email ?? (baseProfile.email ?? (currentUser.email || '')),
                // Ensure notification preferences are preserved
                emailNotifications: updates.emailNotifications ?? baseProfile.emailNotifications,
                smsNotifications: updates.smsNotifications ?? baseProfile.smsNotifications,
                notificationFrequency: updates.notificationFrequency ?? baseProfile.notificationFrequency,
            };
            
            if (import.meta.env.DEV) {
                console.log('[useProfile.updateProfile] Saving profile:', {
                    userId,
                    hasBuyerType: !!fullProfile.buyerType,
                    hasBudgetRange: !!fullProfile.budgetRange,
                    locationsCount: fullProfile.preferredLocations.length,
                    isConfigured: fullProfile.isConfigured,
                    emailNotifications: fullProfile.emailNotifications,
                    smsNotifications: fullProfile.smsNotifications,
                    notificationFrequency: fullProfile.notificationFrequency
                });
            }
            
            // Use api.user.updateProfile which handles all fields and uses upsert correctly
            await api.user.updateProfile(userId, fullProfile);
            
            // CRITICAL: Reset cache and force refetch to ensure we get fresh data
            try {
                runtimeCache.resetResource('profile', userId);
                if (import.meta.env.DEV) {
                    console.log('[useProfile] Cache reset for profile, forcing refetch');
                }
            } catch (err) {
                console.error('[useProfile] Cache reset error:', err);
            }
            
            // Force refetch by calling API directly, bypassing cache check
            try {
                setLoading(true);
                if (import.meta.env.DEV) console.log('[useProfile] [FORCE REFETCH] Profile after update');
                
                // Use the API service to fetch fresh data (bypasses cache)
                const freshProfile = await api.user.getProfile(userId);
                
                if (freshProfile) {
                    runtimeCache.markLoaded('profile', userId);
                    setProfile(freshProfile);
                    
                    if (import.meta.env.DEV) {
                        console.log('[useProfile] [FORCE REFETCH END] Profile loaded:', {
                            emailNotifications: freshProfile.emailNotifications,
                            smsNotifications: freshProfile.smsNotifications,
                            notificationFrequency: freshProfile.notificationFrequency
                        });
                    }
                } else {
                    runtimeCache.markLoaded('profile', userId);
                    setProfile(null);
                }
            } catch (err: any) {
                console.error('[useProfile] Error during force refetch:', err);
                runtimeCache.markFailed('profile', userId);
                // Fallback to regular fetchProfile
                await fetchProfile();
            } finally {
                setLoading(false);
            }
            
            // Return the updated profile from state (will be updated by fetchProfile)
            // Use a small delay to ensure state is updated
            await new Promise(resolve => setTimeout(resolve, 100));
            return profile || fullProfile;
        } catch (err: any) {
            console.error('[useProfile] Error updating profile:', err);
            if (import.meta.env.DEV) {
                console.error('[useProfile] Update error details:', {
                    userId,
                    errorMessage: err.message,
                    errorCode: err.code,
                    errorDetails: err.details,
                    errorHint: err.hint,
                    updates: Object.keys(updates)
                });
            }
            throw err;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Reset cache when userId changes - wrap in try/catch
        try {
            runtimeCache.reset(userId);
        } catch (err) {
            console.error('[useProfile] Cache reset error:', err);
        }

        // Return immediately if !authReady or !userId
        if (!authReady || !userId) {
            if (import.meta.env.DEV) console.log('[useProfile] Effect: waiting for authReady or userId');
            setLoading(false); // Ensure loading is false when waiting
            setTimeoutError(null); // Clear timeout error
            return;
        }

        // Reset timeout error when starting a new fetch
        setTimeoutError(null);
        fetchProfile();
    }, [authReady, userId, fetchProfile]);

    // 5-second safety timeout: if loading is still true after 5000ms, force it to false
    useEffect(() => {
        if (!loading) {
            setTimeoutError(null);
            return;
        }

        const timeoutId = setTimeout(() => {
            if (loading) {
                console.error('[useProfile] TIMEOUT: Profile loading exceeded 5 seconds');
                setTimeoutError('Auth bootstrap timed out');
                setLoading(false); // Force loading to false
            }
        }, 5000);

        return () => clearTimeout(timeoutId);
    }, [loading]);

    // Onboarding completion check: ONLY uses profiles table fields (first_name/last_name)
    // Standardized rule: isProfileComplete = !!(profile?.firstName && profile?.lastName)
    const isProfileComplete = !!(profile?.firstName && profile?.lastName);

    return { profile, loading, error: error || timeoutError, updateProfile, isProfileComplete, refreshProfile: fetchProfile, timeoutError };
};
